import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-full text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 active:scale-[0.98] motion-safe:transition-transform',
  {
    variants: {
      variant: {
        /* Semantic defaults */
        default:
          'bg-primary text-primary-foreground hover:bg-primary/90 active:bg-primary/80',
        secondary:
          'bg-muted text-secondary-foreground hover:bg-muted/80 active:bg-muted/70',
        outline: 'border border-input hover:bg-muted/80 active:bg-muted/70',
        link: 'text-primary underline-offset-4 hover:underline active:opacity-70',
        /* Brand variants */
        brand:
          'bg-brand text-primary-foreground hover:bg-brand-soft active:bg-brand-soft/90',
        'outline-brand':
          'border-2 border-brand text-brand hover:bg-brand hover:text-primary-foreground active:bg-brand-soft active:text-primary-foreground',
        ghost: 'hover:bg-muted/60 text-ink active:bg-muted/80',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 px-3',
        lg: 'h-11 px-8',
        icon: 'h-11 w-11',
        /* Editorial pill — wider horizontal padding, icon badge slot */
        pill: 'h-12 px-6 py-2 text-base gap-2',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
